import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';

const CreateFieldExperience = ({ userData }) => {
  const navigate = useNavigate();

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    title: '',
    date: getCurrentDate(),
    description: '',
    type: 'Hybrid',
    schools: [],
    territories: [],
    allowAllSchools: true,
    allowAllTerritories: true,
    officialPartners: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);
  const [availableSchools, setAvailableSchools] = useState([]);
  const [availableTerritories, setAvailableTerritories] = useState([]);
  const [availableOfficialPartners, setAvailableOfficialPartners] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [showCreatePartnerModal, setShowCreatePartnerModal] = useState(false);
  const [newPartnerData, setNewPartnerData] = useState({ title: '', displayname: '' });
  const [creatingPartner, setCreatingPartner] = useState(false);

  useEffect(() => {
    loadAvailableData();
  }, []);

  const loadAvailableData = async () => {
    try {
      setDataLoading(true);
      const token = localStorage.getItem('token');
      const [schoolsResponse, territoriesResponse, associationsResponse] = await Promise.all([
        axios.get('/api/field-experience-events/schools', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/field-experience-events/territories', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/api/user-management/associations', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setAvailableSchools(schoolsResponse.data.schools);
      setAvailableTerritories(territoriesResponse.data.territories);
      // Filter associations by type "Official Partner"
      const officialPartners = (associationsResponse.data.associations || []).filter(
        assoc => assoc.type === 'Official Partner'
      );
      setAvailableOfficialPartners(officialPartners);
    } catch (error) {
      console.error('Error loading available data:', error);
      setError('Failed to load schools and territories data');
    } finally {
      setDataLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'date' && value) {
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        value = `${year}-${month}-${day}`;
      } else if (typeof value === 'string' && value.includes('T')) {
        value = value.split('T')[0];
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, item, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], item]
        : prev[field].filter(i => i !== item)
    }));
  };

  const handleAddOfficialPartner = (partnerId) => {
    if (!formData.officialPartners.includes(partnerId)) {
      setFormData(prev => ({
        ...prev,
        officialPartners: [...prev.officialPartners, partnerId]
      }));
    }
  };

  const handleRemoveOfficialPartner = (partnerId) => {
    setFormData(prev => ({
      ...prev,
      officialPartners: prev.officialPartners.filter(id => id !== partnerId)
    }));
  };

  const handleCreateOfficialPartner = async () => {
    if (!newPartnerData.title.trim()) {
      setError('Partner title is required');
      return;
    }

    try {
      setCreatingPartner(true);
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/user-management/associations', {
        title: newPartnerData.title,
        displayname: newPartnerData.displayname || newPartnerData.title,
        type: 'Official Partner',
        school: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const newPartner = response.data.association;
      setAvailableOfficialPartners(prev => [...prev, newPartner]);
      handleAddOfficialPartner(newPartner.id);
      setShowCreatePartnerModal(false);
      setNewPartnerData({ title: '', displayname: '' });
    } catch (error) {
      console.error('Error creating official partner:', error);
      setError(error.response?.data?.error || 'Failed to create official partner');
    } finally {
      setCreatingPartner(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/field-experience-events/create-request', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Field experience event request created successfully!');
      setSubmittedSuccessfully(true);

    } catch (error) {
      console.error('Error creating field experience event:', error);
      setError(error.response?.data?.error || 'Failed to create field experience event');
    } finally {
      setLoading(false);
    }
  };

  if (!userData) {
    return <div style={styles.container}>Please log in to create field experience events.</div>;
  }

  if (dataLoading) {
    return <LoadingSpinner />;
  }

  if (submittedSuccessfully) {
    return (
      <div style={styles.container}>
        <div style={styles.success}>{success}</div>
        <div style={styles.successOptions}>
          <p>What would you like to do next?</p>
          <button 
            onClick={() => {
              setSubmittedSuccessfully(false);
              setFormData({
                title: '',
                date: getCurrentDate(),
                description: '',
                type: 'Hybrid',
                schools: [],
                territories: [],
                allowAllSchools: true,
                allowAllTerritories: true,
                officialPartners: []
              });
              setSuccess('');
              setError('');
            }}
            style={styles.optionButton}
          >
            Create Another Event
          </button>
          <button 
            onClick={() => navigate('/my-events')}
            style={styles.optionButton}
          >
            Go to Events Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Create Field Experience Event Request</h1>
      
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <form onSubmit={handleSubmit} style={styles.form}>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Date *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              style={styles.input}
              required
              disabled={loading}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Event Type *</label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              style={styles.select}
              required
              disabled={loading}
            >
              <option value="In-Person">In-Person</option>
              <option value="Virtual">Virtual</option>
              <option value="Hybrid">Hybrid</option>
            </select>
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Event Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            style={styles.input}
            placeholder="Enter event title"
            required
            disabled={loading}
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Description *</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            style={styles.textarea}
            rows="4"
            placeholder="Describe the field experience event..."
            required
            disabled={loading}
          />
        </div>

        <div style={styles.section}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <input
                type="checkbox"
                checked={formData.allowAllSchools}
                onChange={(e) => handleInputChange('allowAllSchools', e.target.checked)}
                style={styles.checkbox}
                disabled={loading}
              />
              Allow All Schools
            </label>
          </div>

          {!formData.allowAllSchools && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Select Schools</label>
              <div style={styles.checkboxContainer}>
                {availableSchools.map(school => (
                  <label key={school.school_id} style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      value={school.school_id.toString()}
                      checked={formData.schools.includes(school.school_id.toString())}
                      onChange={(e) => handleArrayChange('schools', school.school_id.toString(), e.target.checked)}
                      style={styles.checkbox}
                      disabled={loading}
                    />
                    {school.school_name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={styles.section}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <input
                type="checkbox"
                checked={formData.allowAllTerritories}
                onChange={(e) => handleInputChange('allowAllTerritories', e.target.checked)}
                style={styles.checkbox}
                disabled={loading}
              />
              Allow All Territories
            </label>
          </div>

          {!formData.allowAllTerritories && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Select Territories</label>
              <div style={styles.checkboxContainer}>
                {availableTerritories.map(territory => {
                  const schoolsInTerritory = availableSchools.filter(school => 
                    school.territories && school.territories.includes(territory.territory_id)
                  );
                  
                  return (
                    <div key={territory.territory_id} style={styles.territoryItem}>
                      <label style={styles.checkboxItem}>
                        <input
                          type="checkbox"
                          value={territory.territory_id}
                          checked={formData.territories.includes(territory.territory_id)}
                          onChange={(e) => handleArrayChange('territories', territory.territory_id, e.target.checked)}
                          style={styles.checkbox}
                          disabled={loading}
                        />
                        <strong>{territory.title}</strong>
                      </label>
                      {schoolsInTerritory.length > 0 && (
                        <div style={styles.territorySchools}>
                          <em>Schools in this territory:</em>
                          <ul style={styles.schoolList}>
                            {schoolsInTerritory.map(school => (
                              <li key={school.school_id} style={styles.schoolListItem}>
                                {school.school_name}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Official Partners Section */}
        <div style={styles.section}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Official Partner(s)</label>
            <div style={styles.partnersContainer}>
              {formData.officialPartners.map(partnerId => {
                const partner = availableOfficialPartners.find(p => p.id === partnerId);
                if (!partner) return null;
                return (
                  <div key={partnerId} style={styles.partnerItem}>
                    <span>{partner.title || partner.displayname}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveOfficialPartner(partnerId)}
                      style={styles.removeButton}
                      disabled={loading}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              <div style={styles.partnerActions}>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddOfficialPartner(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  style={styles.partnerSelect}
                  disabled={loading}
                >
                  <option value="">Select existing partner...</option>
                  {availableOfficialPartners
                    .filter(p => !formData.officialPartners.includes(p.id))
                    .map(partner => (
                      <option key={partner.id} value={partner.id}>
                        {partner.title || partner.displayname}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowCreatePartnerModal(true)}
                  style={styles.addPartnerButton}
                  disabled={loading}
                >
                  + Create New Partner
                </button>
              </div>
            </div>
          </div>
        </div>

        <button 
          type="submit" 
          disabled={loading}
          style={styles.submitButton}
        >
          {loading ? 'Creating...' : 'Create Event Request'}
        </button>
      </form>

      {/* Create Official Partner Modal */}
      {showCreatePartnerModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Create New Official Partner</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Title *</label>
              <input
                type="text"
                value={newPartnerData.title}
                onChange={(e) => setNewPartnerData({ ...newPartnerData, title: e.target.value })}
                style={styles.input}
                placeholder="Enter partner title"
                disabled={creatingPartner}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Display Name</label>
              <input
                type="text"
                value={newPartnerData.displayname}
                onChange={(e) => setNewPartnerData({ ...newPartnerData, displayname: e.target.value })}
                style={styles.input}
                placeholder="Enter display name (optional)"
                disabled={creatingPartner}
              />
            </div>
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={handleCreateOfficialPartner}
                style={styles.createButton}
                disabled={creatingPartner || !newPartnerData.title.trim()}
              >
                {creatingPartner ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreatePartnerModal(false);
                  setNewPartnerData({ title: '', displayname: '' });
                }}
                style={styles.cancelButton}
                disabled={creatingPartner}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '2rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  title: {
    fontSize: '2rem',
    marginBottom: '2rem',
    color: '#333',
    textAlign: 'center'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  formRow: {
    display: 'flex',
    gap: '1.5rem'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    flex: 1
  },
  label: {
    fontSize: '1rem',
    fontWeight: '500',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  input: {
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    transition: 'border-color 0.3s ease'
  },
  textarea: {
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    resize: 'vertical',
    minHeight: '100px',
    transition: 'border-color 0.3s ease'
  },
  select: {
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    transition: 'border-color 0.3s ease'
  },
  section: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '1.5rem',
    backgroundColor: '#fafafa'
  },
  checkboxContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '0.75rem',
    marginTop: '1rem',
    padding: '1rem',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    backgroundColor: '#fff'
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.9rem',
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '4px',
    transition: 'background-color 0.2s ease'
  },
  checkbox: {
    margin: 0,
    transform: 'scale(1.1)'
  },
  submitButton: {
    padding: '1rem 2rem',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    marginTop: '1rem'
  },
  success: {
    padding: '1rem',
    backgroundColor: '#d4edda',
    color: '#155724',
    borderRadius: '4px',
    marginBottom: '1rem',
    border: '1px solid #c3e6cb'
  },
  error: {
    padding: '1rem',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '4px',
    marginBottom: '1rem',
    border: '1px solid #f5c6cb'
  },
  territoryItem: {
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    marginBottom: '0.75rem'
  },
  territorySchools: {
    marginLeft: '1.5rem',
    marginTop: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '4px'
  },
  schoolList: {
    listStyleType: 'none',
    paddingLeft: '0',
    margin: '0.5rem 0 0 0'
  },
  schoolListItem: {
    marginBottom: '0.25rem',
    padding: '0.25rem 0',
    fontSize: '0.85rem',
    color: '#666'
  },
  successOptions: {
    marginTop: '2rem',
    textAlign: 'center'
  },
  optionButton: {
    padding: '0.75rem 1.5rem',
    margin: '0 1rem',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  partnersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginTop: '0.5rem'
  },
  partnerItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    border: '1px solid #e0e0e0',
    borderRadius: '4px'
  },
  partnerActions: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center'
  },
  partnerSelect: {
    flex: 1,
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.9rem'
  },
  addPartnerButton: {
    padding: '0.5rem 1rem',
    backgroundColor: 'transparent',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'border-color 0.3s ease, color 0.3s ease'
  },
  removeButton: {
    background: 'none',
    border: 'none',
    color: '#dc3545',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '0 0.5rem',
    lineHeight: 1
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '2rem',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalTitle: {
    fontSize: '1.5rem',
    marginBottom: '1.5rem',
    color: '#333'
  },
  modalActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    marginTop: '1.5rem'
  },
  createButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  cancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  }
};

export default CreateFieldExperience;